import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthProvider';
import { useI18n } from '@/i18n/LanguageContext';
import { useSettings } from '@/contexts/SettingsContext';
import { useCar } from '@/contexts/CarContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronRight, LogOut } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { authService } from '@/services/auth';
import { uploadCarImage } from '@/lib/uploadCarImage';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';
import { formatDate } from 'date-fns';
import { Camera, X } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import { useToast } from '@/components/ui/use-toast';
import type { SupportedLocale } from '@/i18n/types';
import { ProfilePhotoCropper } from '@/components/ProfilePhotoCropper';

// Country codes with flags (common countries)
const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+39', country: 'IT', flag: '🇮🇹' },
  { code: '+34', country: 'ES', flag: '🇪🇸' },
  { code: '+31', country: 'NL', flag: '🇳🇱' },
  { code: '+32', country: 'BE', flag: '🇧🇪' },
  { code: '+41', country: 'CH', flag: '🇨🇭' },
  { code: '+43', country: 'AT', flag: '🇦🇹' },
  { code: '+45', country: 'DK', flag: '🇩🇰' },
  { code: '+46', country: 'SE', flag: '🇸🇪' },
  { code: '+47', country: 'NO', flag: '🇳🇴' },
  { code: '+358', country: 'FI', flag: '🇫🇮' },
  { code: '+371', country: 'LV', flag: '🇱🇻' },
  { code: '+372', country: 'EE', flag: '🇪🇪' },
  { code: '+370', country: 'LT', flag: '🇱🇹' },
  { code: '+48', country: 'PL', flag: '🇵🇱' },
  { code: '+420', country: 'CZ', flag: '🇨🇿' },
  { code: '+36', country: 'HU', flag: '🇭🇺' },
  { code: '+7', country: 'RU/KZ', flag: '🇷🇺' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+82', country: 'KR', flag: '🇰🇷' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+64', country: 'NZ', flag: '🇳🇿' },
];

const ProfilePage = () => {
  const { user } = useAuth();
  const { currency, t, language, setLanguage, setCurrency } = useI18n();
  const { settings, updateSettings } = useSettings();
  const { cars } = useCar();
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [editProfileDrawerOpen, setEditProfileDrawerOpen] = useState(false);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editPhoneOpen, setEditPhoneOpen] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [languageDialogOpen, setLanguageDialogOpen] = useState(false);
  const [distanceUnitDialogOpen, setDistanceUnitDialogOpen] = useState(false);
  const [currencyDialogOpen, setCurrencyDialogOpen] = useState(false);
  const [fuelUnitDialogOpen, setFuelUnitDialogOpen] = useState(false);
  const [transferOwnershipDialogOpen, setTransferOwnershipDialogOpen] = useState(false);
  const [exportDataDialogOpen, setExportDataDialogOpen] = useState(false);
  const [supportDrawerOpen, setSupportDrawerOpen] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSubject, setSupportSubject] = useState<'Contact support' | 'Report a bug' | 'Feature request'>('Contact support');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [displayName, setDisplayName] = useState(() => {
    const meta: any = user?.user_metadata || {};
    const full = meta.full_name || meta.name || meta.display_name || (meta.given_name && meta.family_name ? `${meta.given_name} ${meta.family_name}` : null);
    if (full && typeof full === 'string') return full;
    if (user?.email) {
      const handle = user.email.split('@')[0];
      return handle.charAt(0).toUpperCase() + handle.slice(1);
    }
    return 'User';
  });
  const [tempName, setTempName] = useState(displayName);
  
  // Phone number state
  // NOTE: Phone numbers are stored in public.users.phone_number (not auth metadata)
  // This ensures consistency with signup/onboarding flow
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+371');
  const [tempPhoneNumber, setTempPhoneNumber] = useState(phoneNumber);
  const [tempCountryCode, setTempCountryCode] = useState(countryCode);
  
  // Profile photo state
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [customAvatarPath, setCustomAvatarPath] = useState<string | null>(null);
  const loadedUserIdRef = useRef<string | null>(null); // Track which user ID we've loaded for

  const accountCreatedDate = user?.created_at ? formatDate(new Date(user.created_at), 'dd MMM yyyy') : '';

  // Load phone number from public.users.phone_number (not auth metadata)
  // NOTE: Phone numbers are stored in public.users.phone_number for consistency with signup/onboarding
  // auth.users.raw_user_meta_data.phone_number is deprecated for this app
  useEffect(() => {
    const loadPhoneNumber = async () => {
      if (!user) {
        setPhoneNumber('');
        setCountryCode('+371');
        return;
      }

      try {
        const { data: userData, error } = await supabase
          .from('users')
          .select('phone_number')
          .eq('id', user.id)
          .single();

        if (!error && userData?.phone_number) {
          // Parse phone number: if it starts with +, extract country code
          // Format: "+37126123456" or "26123456"
          const fullPhone = userData.phone_number.trim();
          if (fullPhone.startsWith('+')) {
            // Try to match country code from our list (longest match first for accuracy)
            const sortedCodes = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
            const matchedCode = sortedCodes.find(cc => fullPhone.startsWith(cc.code));
            
            if (matchedCode) {
              setCountryCode(matchedCode.code);
              setPhoneNumber(fullPhone.substring(matchedCode.code.length).trim());
            } else {
              // Fallback: try to extract country code (1-4 digits after +)
              const codeMatch = fullPhone.match(/^(\+\d{1,4})/);
              if (codeMatch && codeMatch[1].length > 1) {
                setCountryCode(codeMatch[1]);
                setPhoneNumber(fullPhone.substring(codeMatch[1].length).trim());
              } else {
                // Can't parse, show full number with default country code
                setCountryCode('+371');
                setPhoneNumber(fullPhone);
              }
            }
          } else {
            // No country code prefix, use default
            setCountryCode('+371');
            setPhoneNumber(fullPhone);
          }
        } else {
          // No phone number stored, use defaults
          setPhoneNumber('');
          setCountryCode('+371');
        }
      } catch (error) {
        console.error('Failed to load phone number:', error);
        setPhoneNumber('');
        setCountryCode('+371');
      }
    };

    loadPhoneNumber();
  }, [user?.id]);

  // Load profile photo on mount and when user ID changes (not on every token refresh)
  useEffect(() => {
    const loadProfilePhoto = async () => {
      if (!user) {
        setProfilePhotoUrl(null);
        setCustomAvatarPath(null);
        loadedUserIdRef.current = null;
        return;
      }

      // Skip if we've already loaded for this user ID (prevents re-querying on token refresh)
      if (loadedUserIdRef.current === user.id) {
        return;
      }

      try {
        // 1. First, check for custom uploaded photo in public.users.custom_avatar_url
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('custom_avatar_url')
          .eq('id', user.id)
          .single();

        if (!userError && userData?.custom_avatar_url) {
          // Custom uploaded photo exists - use it
          setCustomAvatarPath(userData.custom_avatar_url);
          try {
            const signedUrlInfo = await getSignedViewUrl(userData.custom_avatar_url);
            setProfilePhotoUrl(signedUrlInfo.url);
            loadedUserIdRef.current = user.id; // Mark as loaded
            return; // Exit early - custom photo takes precedence
          } catch (error) {
            console.error('Failed to load custom profile photo:', error);
            // Fall through to Google avatar fallback
          }
        }

        // 2. Fallback to Google OAuth avatar_url from auth metadata
        const meta: any = user?.user_metadata || {};
        const googleAvatarUrl = meta.avatar_url;
        
        if (googleAvatarUrl) {
          // Google avatar is a direct URL, not a GCS path
          setProfilePhotoUrl(googleAvatarUrl);
          setCustomAvatarPath(null);
        } else {
          // No avatar found
          setProfilePhotoUrl(null);
          setCustomAvatarPath(null);
        }
        
        loadedUserIdRef.current = user.id; // Mark as loaded
      } catch (error) {
        console.error('Failed to load profile photo:', error);
        setProfilePhotoUrl(null);
        setCustomAvatarPath(null);
        loadedUserIdRef.current = user.id; // Mark as loaded even on error to prevent retry loop
      }
    };
    loadProfilePhoto();
  }, [user?.id]); // Only re-run when user ID changes, not on every token refresh

  const handleEditName = async () => {
    if (!user) return;
    
    try {
      const { error } = await supabase.auth.updateUser({
        data: { display_name: tempName }
      });
      
      if (error) throw error;
      
      setDisplayName(tempName);
      setEditNameOpen(false);
    } catch (error) {
      console.error('Failed to update display name:', error);
    }
  };

  const handleEditPhone = async () => {
    if (!user) return;
    
    try {
      // Store phone number in public.users.phone_number (not auth metadata)
      // Combine country code and phone number: "+37126123456"
      // NOTE: auth.users.raw_user_meta_data.phone_number is deprecated for this app
      const fullPhoneNumber = tempCountryCode && tempPhoneNumber 
        ? `${tempCountryCode}${tempPhoneNumber}`.replace(/\s+/g, '')
        : tempPhoneNumber || null;

      const { error } = await supabase
        .from('users')
        .update({ phone_number: fullPhoneNumber })
        .eq('id', user.id);
      
      if (error) throw error;
      
      setPhoneNumber(tempPhoneNumber);
      setCountryCode(tempCountryCode);
      setEditPhoneOpen(false);

      toast({
        title: t('profile.phoneUpdated') || 'Phone updated',
        description: t('profile.phoneUpdatedDesc') || 'Your phone number has been updated successfully.',
      });
    } catch (error) {
      console.error('Failed to update phone number:', error);
      toast({
        title: t('profile.phoneUpdateError') || 'Update failed',
        description: error instanceof Error ? error.message : t('profile.phoneUpdateErrorDesc') || 'Failed to update phone number. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handlePhotoFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Open cropper with selected file
    setSelectedImageFile(file);
    setCropperOpen(true);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = async (croppedFile: File) => {
    if (!user) return;

    setCropperOpen(false);
    setUploadingPhoto(true);
    
    try {
      // The cropped file from canvas is already properly sized (CROP_SIZE x CROP_SIZE = 300x300)
      // and compressed via canvas.toBlob() with quality 0.95
      // Skip additional compression to avoid pixel shifts or aspect ratio changes
      // that could cause mismatch between what user selected and what's displayed

      // Get session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No valid session found');
      }

      // Upload the cropped image directly (already optimized by canvas)
      const path = await uploadCarImage({
        file: croppedFile, // Use cropped file directly, no additional compression
        userId: user.id,
        entityId: user.id,
        type: 'profile',
        accessToken: session.access_token,
      });

      // Store custom avatar path in public.users table
      const { error: updateError } = await supabase
        .from('users')
        .update({ custom_avatar_url: path })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setCustomAvatarPath(path);
      
      // Reset the loaded user ID ref so the photo reloads
      loadedUserIdRef.current = null;
      
      // Get signed URL for display
      const signedUrlInfo = await getSignedViewUrl(path);
      setProfilePhotoUrl(signedUrlInfo.url);
      
      // Mark as loaded for this user
      loadedUserIdRef.current = user.id;

      toast({
        title: t('profile.photoUploaded') || 'Photo uploaded',
        description: t('profile.photoUploadedDesc') || 'Your profile photo has been updated successfully.',
      });
    } catch (error) {
      console.error('Failed to upload profile photo:', error);
      toast({
        title: t('profile.photoUploadError') || 'Upload failed',
        description: error instanceof Error ? error.message : t('profile.photoUploadErrorDesc') || 'Failed to upload profile photo. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingPhoto(false);
      setSelectedImageFile(null);
    }
  };

  const handleCropCancel = () => {
    setCropperOpen(false);
    setSelectedImageFile(null);
  };

  const handleDistanceUnitChange = (unit: 'km' | 'mi') => {
    updateSettings({ distanceUnit: unit });
    setDistanceUnitDialogOpen(false);
  };

  const handleFuelUnitChange = (unit: 'L' | 'gal') => {
    updateSettings({ fuelUnit: unit });
    setFuelUnitDialogOpen(false);
  };

  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
    setCurrencyDialogOpen(false);
  };

  const handleLanguageChange = (newLang: SupportedLocale) => {
    setLanguage(newLang);
    setLanguageDialogOpen(false);
  };

  const handleLogout = async () => {
    try {
      await authService.signOut();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleExportData = () => {
    setExportDataDialogOpen(true);
  };

  const handleSharedAccess = () => {
    navigate('/shared-access');
  };


  const handleTransferOwnership = () => {
    setTransferOwnershipDialogOpen(true);
  };

  const handleNotifyMe = async (feature: 'transferOwnership' | 'exportData') => {
    if (!user) return;

    try {
      // Upsert feature interest (insert or update if exists)
      const { error } = await supabase
        .from('feature_interests')
        .upsert(
          {
            user_id: user.id,
            feature_name: feature,
            notified: false,
          },
          {
            onConflict: 'user_id,feature_name',
          }
        );

      if (error) throw error;

      toast({
        title: t('profile.notifyMeSuccess') || 'Success',
        description: t('profile.notifyMeSuccessDesc') || 'We will notify you when this feature is available.',
      });

      if (feature === 'transferOwnership') {
        setTransferOwnershipDialogOpen(false);
      } else {
        setExportDataDialogOpen(false);
      }
    } catch (error) {
      console.error('Failed to save feature interest:', error);
      toast({
        title: t('profile.notifyMeError') || 'Error',
        description: error instanceof Error ? error.message : t('profile.notifyMeErrorDesc') || 'Failed to save your interest. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleContactSupport = () => {
    setSupportSubject('Contact support');
    setSupportMessage('');
    setSupportDrawerOpen(true);
  };

  const handleReportBug = () => {
    setSupportSubject('Report a bug');
    setSupportMessage('');
    setSupportDrawerOpen(true);
  };

  const handleFeatureRequest = () => {
    setSupportSubject('Feature request');
    setSupportMessage('');
    setSupportDrawerOpen(true);
  };

  const handleSendSupportMessage = async () => {
    if (!supportMessage.trim() || !user?.email) {
      toast({
        title: t('profile.messageRequired') || 'Message required',
        description: t('profile.messageRequiredDesc') || 'Please enter a message before sending.',
        variant: 'destructive',
      });
      return;
    }

    setSendingMessage(true);
    try {
      // Call Supabase Edge Function to send email
      const { data, error } = await supabase.functions.invoke('send-support-email', {
        body: {
          from: user.email,
          subject: supportSubject,
          message: supportMessage,
        },
      });

      if (error) throw error;

      toast({
        title: t('profile.messageSent') || 'Message sent',
        description: t('profile.messageSentDesc') || 'Your message has been sent successfully. We will get back to you soon.',
      });

      setSupportMessage('');
      setSupportDrawerOpen(false);
    } catch (error) {
      console.error('Failed to send support message:', error);
      toast({
        title: t('profile.messageSendError') || 'Failed to send',
        description: error instanceof Error ? error.message : t('profile.messageSendErrorDesc') || 'Failed to send your message. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSendingMessage(false);
    }
  };

  const displayPhone = phoneNumber ? `${countryCode} ${phoneNumber}` : '';

  // Helper function to get display value for settings
  const getLanguageDisplay = () => {
    return language === 'en' ? t('profile.english') : t('profile.latvian');
  };

  const getDistanceUnitDisplay = () => {
    return settings.distanceUnit === 'mi' ? t('profile.miles') : t('profile.kilometers');
  };

  const getCurrencyDisplay = () => {
    return currency === 'USD' ? t('profile.usd') : t('profile.eur');
  };

  const getFuelUnitDisplay = () => {
    return settings.fuelUnit === 'gal' ? t('profile.gallons') : t('profile.liters');
  };

  return (
    <PageLayout title={t('profile.title')} showBackButton onBack={() => navigate(-1)}>
      <div className="space-y-4 pb-16">
        {/* Profile Section - No Card */}
        <div className="flex flex-col items-center py-6 px-4">
          {/* Profile Photo */}
          <div className="relative mb-4">
            {profilePhotoUrl ? (
              <button
                type="button"
                onClick={() => setPhotoModalOpen(true)}
                className="cursor-pointer"
              >
                <img
                  src={profilePhotoUrl}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-2 border-gray-200 hover:opacity-90 transition-opacity"
                />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-200 hover:bg-gray-300 transition-colors disabled:opacity-50"
              >
                <Camera className="w-8 h-8 text-gray-400" />
              </button>
            )}
            {profilePhotoUrl && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute bottom-0 right-0 bg-lime-500 text-white rounded-full p-2 hover:bg-lime-600 transition-colors disabled:opacity-50"
                style={{ fontFamily: 'Lato' }}
              >
                {uploadingPhoto ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="w-4 h-4" />
                )}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoFileSelect}
              className="hidden"
            />
          </div>

          {/* Display Name */}
          <h2 className="text-xl font-bold text-black mb-4" style={{ fontFamily: 'Lato' }}>
            {displayName}
          </h2>

          {/* Edit Profile Button */}
          <Button
            type="button"
            onClick={() => setEditProfileDrawerOpen(true)}
            variant="secondary"
            className="rounded-full px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 bg-white"
            style={{ fontFamily: 'Lato' }}
          >
            {t('profile.editProfile') || 'Edit profile'}
          </Button>
        </div>

        {/* Language Card */}
        <Card className="w-full rounded-[20px] bg-[#FDFDFD]" style={{ boxShadow: '0px 10px 8px -1px #EEEEEE' }}>
          <CardHeader className="p-4 pb-3">
            <CardTitle className="text-black" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '16px', lineHeight: '22px' }}>
              {t('profile.language')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-0">
            <button
              type="button"
              onClick={() => setLanguageDialogOpen(true)}
              className="w-full flex items-center justify-between py-3 text-sm px-2 cursor-pointer hover:bg-gray-50 rounded-xl"
              style={{ fontFamily: 'Lato' }}
            >
              <span className="text-gray-800">{t('profile.selectLanguage')}</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">{getLanguageDisplay()}</span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </button>
          </CardContent>
        </Card>

        {/* Preferences & Units Card */}
        <Card className="w-full rounded-[20px] bg-[#FDFDFD]" style={{ boxShadow: '0px 10px 8px -1px #EEEEEE' }}>
          <CardHeader className="p-4 pb-3">
            <CardTitle className="text-black" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '16px', lineHeight: '22px' }}>
              {t('profile.preferencesUnits')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-0">
            {/* Distance unit */}
            <button
              type="button"
              onClick={() => setDistanceUnitDialogOpen(true)}
              className="w-full flex items-center justify-between py-3 text-sm px-2 cursor-pointer hover:bg-gray-50 rounded-xl"
              style={{ fontFamily: 'Lato' }}
            >
              <span className="text-gray-800">{t('profile.distanceUnit')}</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">{getDistanceUnitDisplay()}</span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </button>

            {/* Currency */}
            <button
              type="button"
              onClick={() => setCurrencyDialogOpen(true)}
              className="w-full flex items-center justify-between py-3 text-sm px-2 cursor-pointer hover:bg-gray-50 rounded-xl"
              style={{ fontFamily: 'Lato' }}
            >
              <span className="text-gray-800">{t('profile.currency')}</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">{getCurrencyDisplay()}</span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </button>

            {/* Fuel unit */}
            <button
              type="button"
              onClick={() => setFuelUnitDialogOpen(true)}
              className="w-full flex items-center justify-between py-3 text-sm px-2 cursor-pointer hover:bg-gray-50 rounded-xl"
              style={{ fontFamily: 'Lato' }}
            >
              <span className="text-gray-800">{t('profile.fuelUnit')}</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">{getFuelUnitDisplay()}</span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </button>
          </CardContent>
        </Card>

        {/* Car Data Sharing & Ownership Card */}
        <Card className="w-full rounded-[20px] bg-[#FDFDFD]" style={{ boxShadow: '0px 10px 8px -1px #EEEEEE' }}>
          <CardHeader className="p-4 pb-3">
            <CardTitle className="text-black" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '16px', lineHeight: '22px' }}>
              {t('profile.carDataSharing')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-0">
            {/* Shared Access */}
            <button
              type="button"
              onClick={handleSharedAccess}
              className="w-full flex items-center justify-between py-3 text-sm cursor-pointer hover:bg-gray-50 rounded-xl px-2"
              style={{ fontFamily: 'Lato' }}
            >
              <span className="text-gray-800">{t('profile.sharedAccess')}</span>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>

            {/* Transfer Ownership */}
            <button
              type="button"
              onClick={handleTransferOwnership}
              className="w-full flex items-center justify-between py-3 text-sm cursor-pointer hover:bg-gray-50 rounded-xl px-2"
              style={{ fontFamily: 'Lato' }}
            >
              <span className="text-gray-800">{t('profile.transferOwnership')}</span>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>

            {/* Export Data */}
            <button
              type="button"
              onClick={handleExportData}
              className="w-full flex items-center justify-between py-3 text-sm cursor-pointer hover:bg-gray-50 rounded-xl px-2"
              style={{ fontFamily: 'Lato' }}
            >
              <span className="text-gray-800">{t('profile.exportData')}</span>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          </CardContent>
        </Card>

        {/* Support & Help Card */}
        <Card className="w-full rounded-[20px] bg-[#FDFDFD]" style={{ boxShadow: '0px 10px 8px -1px #EEEEEE' }}>
          <CardHeader className="p-4 pb-3">
            <CardTitle className="text-black" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '16px', lineHeight: '22px' }}>
              {t('profile.supportHelp')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-0">
            {/* Contact Support */}
            <button
              type="button"
              onClick={handleContactSupport}
              className="w-full flex items-center justify-between py-3 text-sm cursor-pointer hover:bg-gray-50 rounded-xl px-2"
              style={{ fontFamily: 'Lato' }}
            >
              <span className="text-gray-800">{t('profile.contactSupport')}</span>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>

            {/* Report Bug */}
            <button
              type="button"
              onClick={handleReportBug}
              className="w-full flex items-center justify-between py-3 text-sm cursor-pointer hover:bg-gray-50 rounded-xl px-2"
              style={{ fontFamily: 'Lato' }}
            >
              <span className="text-gray-800">{t('profile.reportBug')}</span>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>

            {/* Feature Requests */}
            <button
              type="button"
              onClick={handleFeatureRequest}
              className="w-full flex items-center justify-between py-3 text-sm cursor-pointer hover:bg-gray-50 rounded-xl px-2"
              style={{ fontFamily: 'Lato' }}
            >
              <span className="text-gray-800">{t('profile.featureRequests')}</span>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>

          </CardContent>
        </Card>

        {/* Logout Button */}
        <div className="mt-4 mx-[14px]">
          <Button
            onClick={handleLogout}
            className="w-full py-3 rounded-xl bg-red-500 text-white font-medium text-sm hover:bg-red-600"
            style={{ fontFamily: 'Lato' }}
          >
            {t('profile.logout')}
          </Button>
        </div>
      </div>

      {/* Edit Name Dialog */}
      <Dialog open={editNameOpen} onOpenChange={setEditNameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('profile.displayName')}</DialogTitle>
            <DialogDescription>
              {t('profile.editNameDesc') || 'Update your display name'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              placeholder={t('profile.displayName')}
            />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditNameOpen(false)}>
                {t('actions.cancel')}
              </Button>
              <Button onClick={handleEditName}>
                {t('actions.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Modal */}
      <Dialog open={photoModalOpen} onOpenChange={setPhotoModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('profile.profilePhoto')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {profilePhotoUrl ? (
              <div className="flex justify-center">
                <img
                  src={profilePhotoUrl}
                  alt="Profile"
                  className="w-full max-w-sm h-auto rounded-lg object-cover"
                />
              </div>
            ) : (
              <div className="flex justify-center py-12">
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center">
                  <Camera className="w-16 h-16 text-gray-400" />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPhotoModalOpen(false)}>
                {t('actions.close')}
              </Button>
              <Button
                onClick={() => {
                  setPhotoModalOpen(false);
                  // Small delay to ensure modal closes before opening file picker
                  setTimeout(() => {
                    fileInputRef.current?.click();
                  }, 100);
                }}
                disabled={uploadingPhoto}
              >
                {t('profile.changePhoto')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Drawer */}
      <Drawer open={editProfileDrawerOpen} onOpenChange={setEditProfileDrawerOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{t('profile.editProfile') || 'Edit profile'}</DrawerTitle>
            <DrawerDescription>
              {t('profile.editProfileDesc') || 'Update your profile information'}
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-4 space-y-4">
            {/* Display name — editable */}
            <button
              type="button"
              onClick={() => {
                setEditNameOpen(true);
              }}
              className="w-full text-left text-sm text-gray-800 flex justify-between items-center py-3 cursor-pointer hover:bg-gray-50 rounded-xl px-2"
              style={{ fontFamily: 'Lato' }}
            >
              <span>{t('profile.displayName')}</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">{displayName}</span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </button>

            {/* Email — read only for now */}
            <div className="w-full text-left text-sm text-gray-800 flex justify-between items-center py-3 px-2">
              <span>{t('profile.email')}</span>
              <span className="text-gray-500">{user?.email || ''}</span>
            </div>

            {/* Phone number — editable */}
            <button
              type="button"
              onClick={() => {
                setTempPhoneNumber(phoneNumber);
                setTempCountryCode(countryCode);
                setEditPhoneOpen(true);
              }}
              className="w-full text-left text-sm text-gray-800 flex justify-between items-center py-3 cursor-pointer hover:bg-gray-50 rounded-xl px-2"
              style={{ fontFamily: 'Lato' }}
            >
              <span>{t('profile.phoneNumber')}</span>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">{displayPhone || t('profile.phoneNumberPlaceholder')}</span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </button>

            {/* Account created */}
            <div className="text-sm text-gray-800 flex justify-between items-center py-2 px-2" style={{ fontFamily: 'Lato' }}>
              <span>{t('profile.accountCreated')}</span>
              <span className="text-gray-500">{accountCreatedDate}</span>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Edit Phone Dialog */}
      <Dialog open={editPhoneOpen} onOpenChange={setEditPhoneOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('profile.phoneNumber')}</DialogTitle>
            <DialogDescription>
              {t('profile.editPhoneDesc') || 'Update your phone number'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('profile.countryCode')}</Label>
              <Select
                value={tempCountryCode}
                onValueChange={setTempCountryCode}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_CODES.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.flag} {country.code} ({country.country})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('profile.phoneNumber')}</Label>
              <Input
                type="tel"
                value={tempPhoneNumber}
                onChange={(e) => setTempPhoneNumber(e.target.value)}
                placeholder={t('profile.phoneNumberPlaceholder')}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setEditPhoneOpen(false)}>
                {t('actions.cancel')}
              </Button>
              <Button onClick={handleEditPhone}>
                {t('actions.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Language Dialog */}
      <Dialog open={languageDialogOpen} onOpenChange={setLanguageDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('profile.language')}</DialogTitle>
            <DialogDescription>
              {t('profile.selectLanguage')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => handleLanguageChange('en')}
              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                language === 'en' ? 'border-lime-500 bg-lime-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span style={{ fontFamily: 'Lato' }}>{t('profile.english')}</span>
                {language === 'en' && <span className="text-lime-500">✓</span>}
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleLanguageChange('lv')}
              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                language === 'lv' ? 'border-lime-500 bg-lime-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span style={{ fontFamily: 'Lato' }}>{t('profile.latvian')}</span>
                {language === 'lv' && <span className="text-lime-500">✓</span>}
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Distance Unit Dialog */}
      <Dialog open={distanceUnitDialogOpen} onOpenChange={setDistanceUnitDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('profile.distanceUnit')}</DialogTitle>
            <DialogDescription>
              Select your preferred distance unit
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => handleDistanceUnitChange('km')}
              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                settings.distanceUnit === 'km' ? 'border-lime-500 bg-lime-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span style={{ fontFamily: 'Lato' }}>{t('profile.kilometers')}</span>
                {settings.distanceUnit === 'km' && <span className="text-lime-500">✓</span>}
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleDistanceUnitChange('mi')}
              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                settings.distanceUnit === 'mi' ? 'border-lime-500 bg-lime-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span style={{ fontFamily: 'Lato' }}>{t('profile.miles')}</span>
                {settings.distanceUnit === 'mi' && <span className="text-lime-500">✓</span>}
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Currency Dialog */}
      <Dialog open={currencyDialogOpen} onOpenChange={setCurrencyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('profile.currency')}</DialogTitle>
            <DialogDescription>
              Select your preferred currency
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => handleCurrencyChange('EUR')}
              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                currency === 'EUR' ? 'border-lime-500 bg-lime-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span style={{ fontFamily: 'Lato' }}>{t('profile.eur')}</span>
                {currency === 'EUR' && <span className="text-lime-500">✓</span>}
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleCurrencyChange('USD')}
              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                currency === 'USD' ? 'border-lime-500 bg-lime-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span style={{ fontFamily: 'Lato' }}>{t('profile.usd')}</span>
                {currency === 'USD' && <span className="text-lime-500">✓</span>}
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fuel Unit Dialog */}
      <Dialog open={fuelUnitDialogOpen} onOpenChange={setFuelUnitDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('profile.fuelUnit')}</DialogTitle>
            <DialogDescription>
              Select your preferred fuel unit
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => handleFuelUnitChange('L')}
              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                settings.fuelUnit === 'L' ? 'border-lime-500 bg-lime-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span style={{ fontFamily: 'Lato' }}>{t('profile.liters')}</span>
                {settings.fuelUnit === 'L' && <span className="text-lime-500">✓</span>}
              </div>
            </button>
            <button
              type="button"
              onClick={() => handleFuelUnitChange('gal')}
              className={`w-full text-left p-3 rounded-lg border-2 transition-colors ${
                settings.fuelUnit === 'gal' ? 'border-lime-500 bg-lime-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span style={{ fontFamily: 'Lato' }}>{t('profile.gallons')}</span>
                {settings.fuelUnit === 'gal' && <span className="text-lime-500">✓</span>}
              </div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Ownership Dialog */}
      <Dialog open={transferOwnershipDialogOpen} onOpenChange={setTransferOwnershipDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('profile.featureComingSoon') || 'Feature coming soon'}</DialogTitle>
            <DialogDescription>
              {t('profile.transferOwnership')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600" style={{ fontFamily: 'Lato' }}>
              {t('profile.transferOwnershipDesc') || 'Here you will be able to transfer all car related data to another person in case you, e.g. sell your car.'}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setTransferOwnershipDialogOpen(false)}>
                {t('actions.close')}
              </Button>
              <Button onClick={() => handleNotifyMe('transferOwnership')}>
                {t('profile.notifyMe') || 'Notify me'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Export Data Dialog */}
      <Dialog open={exportDataDialogOpen} onOpenChange={setExportDataDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('profile.featureComingSoon') || 'Feature coming soon'}</DialogTitle>
            <DialogDescription>
              {t('profile.exportData')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600" style={{ fontFamily: 'Lato' }}>
              {t('profile.exportDataDesc') || 'Export all your car data including service records, expenses, and more.'}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setExportDataDialogOpen(false)}>
                {t('actions.close')}
              </Button>
              <Button onClick={() => handleNotifyMe('exportData')}>
                {t('profile.notifyMe') || 'Notify me'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Support Message Drawer */}
      <Drawer open={supportDrawerOpen} onOpenChange={setSupportDrawerOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{supportSubject}</DrawerTitle>
            <DrawerDescription>
              {t('profile.supportMessageDesc') || 'Send us a message and we will get back to you as soon as possible.'}
            </DrawerDescription>
          </DrawerHeader>
          <div className="p-4 space-y-4">
            {/* Support Note */}
            <div className="rounded-lg p-3" style={{ backgroundColor: '#eff2fc', border: '1px solid #7c9ffa' }}>
              <p className="text-sm" style={{ fontFamily: 'Lato', color: '#003b8f' }}>
                {t('profile.supportNote') || 'Support is currently available in Latvian or English. We will try to get back to you within 48 hours.'}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="support-message" style={{ fontFamily: 'Lato' }}>
                {t('profile.message') || 'Message'}
              </Label>
              <Textarea
                id="support-message"
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                placeholder={t('profile.messagePlaceholder') || 'Type your message here...'}
                className="min-h-[200px] resize-none"
                style={{ fontFamily: 'Lato' }}
                disabled={sendingMessage}
              />
            </div>
            <div className="flex justify-between gap-2">
              <Button
                variant="secondary"
                onClick={() => {
                  setSupportDrawerOpen(false);
                  setSupportMessage('');
                }}
                disabled={sendingMessage}
                className="flex-1"
              >
                {t('actions.cancel')}
              </Button>
              <Button
                onClick={handleSendSupportMessage}
                disabled={sendingMessage || !supportMessage.trim()}
                className="flex-1"
              >
                {sendingMessage ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    {t('profile.sending') || 'Sending...'}
                  </>
                ) : (
                  t('profile.send') || 'Send'
                )}
              </Button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Profile Photo Cropper */}
      {selectedImageFile && (
        <ProfilePhotoCropper
          imageFile={selectedImageFile}
          onCrop={handleCropComplete}
          onCancel={handleCropCancel}
          isOpen={cropperOpen}
        />
      )}
    </PageLayout>
  );
};

export default ProfilePage;
